import { useState, type ReactNode } from "react";

import { ExportMenu, FilterBar, type FilterSpec } from "@/components/data";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { applyFilters, parseFilters, type FilterValues } from "@/lib/filters";

import type { Market } from "./api";
import { MARKET_LABEL } from "./format";
import { CHANNEL_LABEL, downloadCsv, exportMessage, LOGS_COPY, marketplaceLabel, notApplied, searchForLogPeriod, skuLabel, storeName, type FulfilmentView, type LogPane, type LogSku, type PlaceFacets, type PlaceParams } from "./logs";
import { PeriodPicker } from "./PeriodPicker";

const COUNT = new Intl.NumberFormat("en-GB");
const F = LOGS_COPY.filters;
const NO_BOUNDS = { from: null, to: null };

type Option = { value: string; label: string };
/** The options with the URL's value kept: a value carried from the Price Run that this log does not hold is shown, so the bar
 * never reads "All" over a filter that is narrowing the table (the table then says no entry matches, with Clear). */
export function keepValue(options: Option[], value: FilterValues[string]): Option[] {
  return typeof value === "string" && value !== "" && !options.some((o) => o.value === value) ? [...options, { value, label: value }] : options;
}

/** The module's place filters in the Price Run's order and words (with the Region the Logs add), then the SKU: every pane shows
 * the same eight. */
export function placeFilterSpecs(facets: PlaceFacets & { markets: Market[]; skus: LogSku[] }, values: FilterValues): FilterSpec[] {
  const choice = (name: string, label: string, options: Option[]): FilterSpec => ({ name, label, kind: "choice", anyLabel: "All", options: keepValue(options, values[name]) });
  return [
    choice("product", F.product, (facets.products ?? []).map((p) => ({ value: p, label: p }))),
    choice("variant", F.variant, (facets.variants ?? []).map((v) => ({ value: v, label: v }))),
    choice("market", F.market, facets.markets.map((m) => ({ value: m, label: MARKET_LABEL[m] }))),
    choice("channel", F.channel, (facets.channels ?? []).map((c) => ({ value: CHANNEL_LABEL[c], label: CHANNEL_LABEL[c] }))),
    choice("store", F.store, (facets.stores ?? []).map((s) => ({ value: storeName(s), label: storeName(s) }))),
    choice("location", F.location, (facets.locations ?? []).map((l) => ({ value: l, label: l }))),
    choice("marketplace", F.marketplace, (facets.marketplaces ?? []).map((m) => ({ value: marketplaceLabel(m), label: marketplaceLabel(m) }))),
    choice("sku", F.sku, facets.skus.map((s) => ({ value: s.xero_sku, label: skuLabel(s) }))),
  ];
}

/** Writes the CSV through `save`, or answers the sentence that says why it was not written (the toolbar's Export, kept apart
 * from the component so it is tested without a browser). */
export async function runExport(exportCsv: () => Promise<{ text: string; name: string }>, areaName: string,
  save: (text: string, name: string) => void = downloadCsv): Promise<string | null> {
  try {
    const file = await exportCsv();
    save(file.text, file.name);
    return null;
  } catch (error) {
    return exportMessage(error, areaName);
  }
}

type Props = {
  areaName: string; search: Record<string, unknown>; go: (next: Record<string, unknown>) => void;
  /** The pane, for the no-place line's words (C1 v1.36). */
  pane: LogPane;
  params: PlaceParams & { fulfilment?: FulfilmentView }; facets: PlaceFacets & { markets: Market[]; skus: LogSku[] }; paneFilters: FilterSpec[];
  /** The filters as the API applied them (its `filters` echo): a sent filter missing from it was ignored by an older API. */
  applied: object;
  total: number; trailing: string; exportCsv: () => Promise<{ text: string; name: string }>; children?: ReactNode;
};

/**
 * The filter row and the toolbar every Logs pane shares (C1 v1.32): the place filters, the SKU and the pane's own filters in one
 * bar whose Clear also clears the period; under it the period, any pane control (the Costs pane's fulfilment switch) and the
 * export of every matching entry. Exporting changes nothing, so it is offered at view (read-only is not no-access).
 */
export function LogsToolbar({ areaName, pane, search, go, params, facets, paneFilters, applied, total, trailing, exportCsv, children }: Props) {
  const values = parseFilters(search);
  const ignored = notApplied(params, applied);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const write = async () => {
    setBusy(true);
    setProblem(null);
    setProblem(await runExport(exportCsv, areaName));
    setBusy(false);
  };
  return (
    <div className="flex flex-col gap-2">
      <FilterBar layout="inline" filters={[...placeFilterSpecs(facets, values), ...paneFilters]} values={values} clears={["period"]}
        onChange={(next) => go({ ...applyFilters(search, next), pp_lpage: undefined })} trailing={trailing} />
      <div className="flex flex-wrap items-center gap-2">
        <PeriodPicker from={params.from} to={params.to} book={NO_BOUNDS} today={new Date()} copy={LOGS_COPY.period}
          onChange={(from, to) => go(searchForLogPeriod(search, from, to))} />
        {children}
        <span className="ml-auto flex items-center gap-2">
          {busy && <span role="status" className="text-xs text-ks-grey-1">{LOGS_COPY.export.busy(COUNT.format(total))}</span>}
          <ExportMenu decision="view" formats={[{ key: "csv", label: LOGS_COPY.export.format(COUNT.format(total)) }]} onExport={() => void write()} />
        </span>
      </div>
      {(params.location || params.marketplace) && <p className="text-xs text-ks-grey-1" data-no-place>{LOGS_COPY.noPlace[pane]}</p>}
      {ignored.length > 0 && <Alert role="note"><AlertDescription>{LOGS_COPY.olderApi(ignored)}</AlertDescription></Alert>}
      {problem && <Alert role="alert" variant="destructive"><AlertDescription>{problem}</AlertDescription></Alert>}
    </div>
  );
}

/** A pane that could not be read: the module's sentence for the error, and Retry (reading again changes nothing). */
export function LogsError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Alert role="alert" variant="destructive">
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
        <span>{message}</span>
        <Button size="sm" variant="outline" data-nonmutating onClick={onRetry}>{LOGS_COPY.retry}</Button>
      </AlertDescription>
    </Alert>
  );
}
